import { Checkbox, Divider, Elevation } from '@blueprintjs/core';
import { PriceTreeLabel, ReferenceReactionTreeLabel, SynthesisTree, styled } from '@moleculeone/ui';
import { ReportItemType } from '@retro-ui/shared';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import ContextualCard from '@f/components/shared/contextual-card/ContextualCard';
import Reaction from '@f/components/shared/reaction/Reaction';

import CommentContext from './CommentContext';
import MovingButtons from './MovingButtons';
import RemoveButton from './RemoveButton';
import cls from './ReportItem.module.css';
import { SynthesisTreeInfo } from './SynthesisTreeInfo';
import withMovingSpace from './with-moving-space';

const SupplierLinksContainer = styled('div', {
  display: 'flex',
  gap: '2rem',
  padding: '0.25rem 0',
});

class ReportItem extends PureComponent {
  static propTypes = {
    data: PropTypes.object,
    isLast: PropTypes.bool,
    isThisDesigning: PropTypes.bool,
    isSelectionMode: PropTypes.bool,
    isSelected: PropTypes.bool,
    isMovingMode: PropTypes.bool,
    isThisMoving: PropTypes.bool,
    movingOrdinal: PropTypes.number,
    onToggleSelected: PropTypes.func,
    onMovingModeSwitch: PropTypes.func,
    onMove: PropTypes.func,
    onMoveStep: PropTypes.func,
    onRemove: PropTypes.func,
    onCommentUpdate: PropTypes.func,
  };

  transformReactionData = reactionData => {
    const { productSmiles, substrateSmiles, productToMap, substratesToMap } = reactionData;

    return {
      product: {
        smiles: productSmiles,
      },
      substrates: substrateSmiles.map(smiles => ({ smiles })),
      productToMap,
      substratesToMap,
      highlights: [],
    };
  };

  onToggleSelected = () => {
    const { data } = this.props;
    this.props.onToggleSelected(data);
  };

  renderItem = () => {
    const { data, isThisDesigning } = this.props;
    const { type, payload } = data;

    switch (type) {
      case ReportItemType.REACTION: {
        return {
          'title': 'Reaction',
          'component': (
            <>
              <ContextualCard.Main className={cls.mainContext}>
                <Reaction {...this.transformReactionData(payload)} />
              </ContextualCard.Main>

              <ContextualCard.Bottom className={cls.bottomContext} border>
                <CommentContext data={data} disabled={!isThisDesigning} onCommentUpdate={this.props.onCommentUpdate} />
              </ContextualCard.Bottom>
            </>
          ),
          'data-cy': 'reaction',
        };
      }

      case ReportItemType.SYNTHESIS_TREE: {
        const resultLink =
          payload.searchId &&
          `${window.location.origin}/dashboard/batch/${payload.searchId}/result/${payload.searchResultInfo.id}`;

        const reactionNameMap =
          payload.referenceReactions?.length > 0 &&
          payload.referenceReactions.reduce((acc, { reactionId }, idx) => {
            acc[reactionId] = idx + 1;
            return acc;
          }, {});

        const startingMaterialsNameMap =
          payload.startingMaterials?.length > 0 &&
          payload.startingMaterials.reduce((acc, { id }, idx) => {
            acc[id] = String.fromCharCode(97 + idx).toUpperCase();
            return acc;
          }, {});

        return {
          'title': (
            <>
              Synthesis tree{' '}
              {resultLink && (
                <span className={cls.resultLink}>
                  - <a href={resultLink}>{resultLink}</a>
                </span>
              )}
            </>
          ),
          'component': (
            <>
              <ContextualCard.Main className={cls.mainContext}>
                <SynthesisTree
                  tree={payload.nodes}
                  renderPointContext={{
                    common:
                      reactionNameMap &&
                      (props => <ReferenceReactionTreeLabel labelMap={reactionNameMap} {...props} />),
                    end:
                      startingMaterialsNameMap &&
                      (props => <PriceTreeLabel labelMap={startingMaterialsNameMap} {...props} />),
                  }}
                />
              </ContextualCard.Main>

              <ContextualCard.Bottom className={cls.bottomContext}>
                {Object.keys(payload.nodes).length === 1 && (
                  <div className={cls.borderTop}>
                    <h3 className={cls.synthesisInfoTitle}>Target commercially available</h3>
                  </div>
                )}

                {payload.searchResultInfo && <SynthesisTreeInfo className={cls.borderTop} data={payload} />}

                <CommentContext
                  data={data}
                  disabled={!isThisDesigning}
                  onCommentUpdate={this.props.onCommentUpdate}
                  className={cls.borderTop}
                />

                {reactionNameMap && (
                  <div className={cls.borderTop}>
                    <h4 className={cls.synthesisInfoTitle}>Reaction references</h4>
                    <ul className={cls.synthesisInfoList}>
                      {payload.referenceReactions.map(({ link, reactionId }) => (
                        <li key={reactionId}>
                          <span>{reactionNameMap[reactionId]}.</span> <a href={link}>{link}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {startingMaterialsNameMap && (
                  <div className={cls.borderTop}>
                    <h4 className={cls.synthesisInfoTitle}>Starting materials</h4>
                    <ul className={cls.synthesisInfoList}>
                      {payload.startingMaterials.map(compound => (
                        <li key={compound.id}>
                          <span>{startingMaterialsNameMap[compound.id]}.</span> {compound.smiles} — {compound.price}
                          {compound.supplierLinks?.length > 0 && (
                            <SupplierLinksContainer>
                              {compound.supplierLinks.map(supplierLink => (
                                <a
                                  key={supplierLink.providerName}
                                  href={supplierLink.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {supplierLink.providerName}
                                </a>
                              ))}
                            </SupplierLinksContainer>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </ContextualCard.Bottom>
            </>
          ),
          'data-cy': 'synthesis-tree',
        };
      }

      default:
        return null;
    }
  };

  render() {
    const { data, isLast, isThisDesigning, isSelectionMode, isSelected, isMovingMode, isThisMoving } = this.props;

    if (!data) {
      return null;
    }

    const item = this.renderItem();

    if (!item) {
      return null;
    }

    const isSelectCheckboxRendered = isThisDesigning && !isMovingMode;

    return (
      <ContextualCard
        className={cls.contextualCard}
        elevation={isThisMoving && Elevation.FOUR}
        data-cy={`card-report-item-${item['data-cy']}`}
      >
        <ContextualCard.Top className={cls.topContext}>
          {isSelectCheckboxRendered && (
            <Checkbox
              className={cx(cls.selectionCheckbox, { [cls.visibleOnlyOnCardHover]: !isSelectionMode })}
              large
              checked={isSelected}
              onChange={this.onToggleSelected}
              data-cy="checkbox-report-item"
            />
          )}

          <span>{item.title}</span>
        </ContextualCard.Top>

        <ContextualCard.Floating className={cx(cls.movingActions, { [cls.visibleOnlyOnCardHover]: !isMovingMode })}>
          {!isMovingMode && (
            <>
              <RemoveButton data={data} disabled={!isThisDesigning} onRemove={this.props.onRemove} />

              <Divider />
            </>
          )}

          <MovingButtons
            data={data}
            isLast={isLast}
            isMovingMode={isMovingMode}
            isThisMoving={isThisMoving}
            disabled={!isThisDesigning}
            onMovingModeSwitch={this.props.onMovingModeSwitch}
            onMoveStep={this.props.onMoveStep}
          />
        </ContextualCard.Floating>

        {React.Children.count(item.component.props.children) > 1 ? item.component.props.children : item.component}
      </ContextualCard>
    );
  }
}

export default withMovingSpace(ReportItem);
